import { memo, useCallback, useMemo } from "react";

import type { LocalCancelFeeTransferInfo } from "@/lib/gql/__generated__/graphql";

import { Button } from "@/components/ui/button/button";

import { LocalCancelFeeTransferRejectModal } from "../../components";

type Props = {
	// 支払い予定額
	willCancelPaymentAmount: number;
	// 現地決済のキャンセル料金情報
	localCancelFeeTransferInfo?: LocalCancelFeeTransferInfo;
	//
	// 現地決済のキャンセル料金支払いを承認する
	onUpdateReservationLocalCancelFeeTransferAccept: () => Promise<void>;
	// 現地決済のキャンセル料金支払いを非承認する
	onUpdateReservationLocalCancelFeeTransferReject: (
		insufficientAmount: number,
		rejectReason: string,
	) => Promise<void>;
};

export const CancellationFeePaymentStatusOfOperationForReservationComposition = memo(
	({
		willCancelPaymentAmount,
		onUpdateReservationLocalCancelFeeTransferAccept,
		onUpdateReservationLocalCancelFeeTransferReject,
		localCancelFeeTransferInfo,
	}: Props) => {
		/**
		 * 承認を押下した場合
		 */
		const handleClickAccept = useCallback(() => {
			const confirmText = `この操作は取り消すことが出来ません。本当に承認しますか？\n（銀行振り込みが行われていることを必ずご確認ください。）`;
			if (window.confirm(confirmText)) {
				/**
				 * 現地決済のキャンセル料金支払いを承認する
				 */
				onUpdateReservationLocalCancelFeeTransferAccept();
			}
		}, [onUpdateReservationLocalCancelFeeTransferAccept]);

		/**
		 * キャンセル料金のテキスト
		 */
		const willCancelPaymentAmountText = useMemo(() => {
			return `¥ ${willCancelPaymentAmount.toLocaleString()}`;
		}, [willCancelPaymentAmount]);

		/**
		 * 不足分請求金額のテキスト
		 */
		const insufficientAmountText = useMemo(() => {
			return `¥ ${localCancelFeeTransferInfo?.lackPrice?.toLocaleString() ?? 0}`;
		}, [localCancelFeeTransferInfo?.lackPrice]);

		/**
		 * キャンセル料金の振り込み情報の表示制御
		 */
		const canShowTransferInfo = useMemo(() => {
			return localCancelFeeTransferInfo?.transferAccountName || localCancelFeeTransferInfo?.transferDate;
		}, [localCancelFeeTransferInfo?.transferAccountName, localCancelFeeTransferInfo?.transferDate]);

		return (
			<div className="space-y-2">
				<div className="flex items-center gap-x-5">
					<p>キャンセル料金支払い状況</p>

					<div className="flex gap-3">
						<Button type="button" size="sm" onClick={handleClickAccept}>
							承認
						</Button>

						<LocalCancelFeeTransferRejectModal
							onUpdateReservationLocalCancelFeeTransferReject={
								onUpdateReservationLocalCancelFeeTransferReject
							}
						/>
					</div>
				</div>

				<div className="space-y-2">
					<p className="text-sm">支払い予定金額：{willCancelPaymentAmountText}</p>
					{canShowTransferInfo ? (
						<>
							<p className="text-sm">お支払い者名: {localCancelFeeTransferInfo?.transferAccountName}</p>
							<p className="text-sm">お支払い日時: {localCancelFeeTransferInfo?.transferDate}</p>
							<p className="text-sm">不足分請求金額: {insufficientAmountText}</p>
						</>
					) : (
						<p className="text-sm">お支払い情報：なし</p>
					)}
				</div>
			</div>
		);
	},
);
